import { CircularProgress, CssBaseline, GlobalStyles, ThemeProvider, createTheme } from "@mui/material";
import { BrowserRouter } from "react-router-dom";
import AppRoutes from "./AppRoutes";
import { Provider } from 'react-redux';
import { Amplify } from 'aws-amplify';
import awsconfig from './aws-exports';
import { Toaster } from "react-hot-toast";
import store, { persistor } from './redux/store'
import { PersistGate } from "redux-persist/integration/react";
import { AlertProvider } from "./AlertContext";
import { ThemeProvider as ThemeProviderAmplify, Authenticator, useTheme } from "@aws-amplify/ui-react";
import React from "react";

delete awsconfig.aws_cognito_identity_pool_id;
Amplify.configure(awsconfig);

const muiTheme = createTheme({
  typography: {
    fontFamily: 'Poppins, sans-serif',
    h4: { fontWeight: 700, letterSpacing: '-0.5px' },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 500, lineHeight: 1.6 },
    subtitle2: { fontWeight: 500 },
    body1: { fontWeight: 400 },
    body2: { fontWeight: 400, lineHeight: 1.7 },
  },
  palette: {
    primary: {
      main: '#EE2D41',
      dark: '#C41E30',
      light: '#F55566',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#1565C0',
      dark: '#0D47A1',
      light: '#1976D2',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#F5F7FA',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1A2B3C',
      secondary: '#6B7A8D',
    },
    divider: 'rgba(0,0,0,0.08)',
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: '#EFF2F7',
          '& fieldset': {
            borderColor: 'transparent',
          },
          '&:hover fieldset': {
            borderColor: 'rgba(0,0,0,0.15) !important',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#0B1B2E !important',
            borderWidth: '1.5px !important',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: 14,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontFamily: 'Poppins, sans-serif',
          fontWeight: 600,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(0,0,0,0.08)',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          paddingLeft: 20,
          paddingRight: 20,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: 'Poppins, sans-serif',
          fontWeight: 500,
        },
      },
    },
  },
});

function App() {

  const { tokens } = useTheme();

  const customTheme = {
    name: 'custom-theme',
    tokens: {
      fonts: {
        default: {
          variable: { value: 'Poppins, sans-serif' },
          static: { value: 'Poppins, sans-serif' },
        },
      },
      components: {
        authenticator: {
          router: {
            boxShadow: 'none',
            borderWidth: '0',
            borderRadius: '0',
          },
          form: {
            padding: `${tokens.space.xs} 0`,
          },
        },
        button: {
          primary: {
            backgroundColor: { value: '#000000' },
            borderColor: { value: '#000000' },
            color: { value: '#FFFFFF' },
            fontFamily: { value: 'Poppins, sans-serif' },
            fontWeight: { value: '600' },
            borderRadius: { value: '12px' },
            _hover: {
              backgroundColor: { value: '#1A2550' },
              borderColor: { value: '#1A2550' },
            },
            _active: {
              backgroundColor: { value: '#090D28' },
              borderColor: { value: '#090D28' },
            },
          },
          link: {
            color: { value: '#4361EE' },
            fontFamily: { value: 'Poppins, sans-serif' },
            _hover: {
              backgroundColor: { value: 'rgba(67,97,238,0.07)' },
              color: { value: '#3A52D4' },
            },
            _active: {
              backgroundColor: { value: 'rgba(67,97,238,0.07)' },
              color: { value: '#3A52D4' },
            },
          },
        },
        fieldcontrol: {
          backgroundColor: { value: '#FFFFFF' },
          borderColor: { value: '#E5E7EB' },
          borderRadius: { value: '12px' },
          fontFamily: { value: 'Poppins, sans-serif' },
          color: { value: '#0B1B2E' },
          _focus: {
            boxShadow: '0 0 0 4px rgba(67,97,238,0.1)',
            borderColor: { value: '#4361EE' },
          },
        },
      },
    },
  };

  return (
    <React.Fragment>
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        <GlobalStyles styles={{
          /* ── Keyframe animations ── */
          '@keyframes fadeSlideUp': {
            from: { opacity: 0, transform: 'translateY(18px)' },
            to:   { opacity: 1, transform: 'translateY(0)' },
          },
          '@keyframes inputGlow': {
            '0%':   { boxShadow: '0 0 0 0 rgba(67,97,238,0.15)' },
            '50%':  { boxShadow: '0 0 0 5px rgba(67,97,238,0.06)' },
            '100%': { boxShadow: '0 0 0 4px rgba(67,97,238,0.1)' },
          },
          '@keyframes btnShimmer': {
            '0%':   { backgroundPosition: '0% 50%' },
            '100%': { backgroundPosition: '200% 50%' },
          },
          '@keyframes accentPulse': {
            '0%, 100%': { opacity: 1 },
            '50%':      { opacity: 0.7 },
          },

          /* ── Authenticator shell ── */
          '[data-amplify-router]': {
            backgroundColor: 'transparent !important',
            border: 'none !important',
            boxShadow: 'none !important',
            padding: '0 !important',
            width: '100% !important',
          },
          '[data-amplify-authenticator]': {
            width: '100% !important',
            display: 'block !important',
          },

          /* ── Form entry animation & alignment ── */
          '[data-amplify-authenticator] form': {
            animation: 'fadeSlideUp 0.45s cubic-bezier(0.22,1,0.36,1) both',
            width: '100% !important',
          },

          /* ── Inner flex column that Amplify renders ── */
          '[data-amplify-authenticator] .amplify-flex': {
            width: '100% !important',
          },
          '[data-amplify-authenticator] .amplify-authenticator__column': {
            width: '100% !important',
            padding: '0 !important',
            gap: '0 !important',
          },

          /* ── Heading (non-sign-in steps: Reset Password, Confirm, etc.) ── */
          '[data-amplify-authenticator] .amplify-heading': {
            fontFamily: 'Poppins, sans-serif !important',
            fontWeight: '700 !important',
            color: '#0B1B2E !important',
            fontSize: '26px !important',
            letterSpacing: '-0.4px !important',
            lineHeight: '1.25 !important',
            marginTop: '0 !important',
            marginBottom: '24px !important',
            paddingTop: '0 !important',
            display: 'block !important',
            width: '100% !important',
          },

          /* ── Labels — hidden, placeholders used instead ── */
          '[data-amplify-authenticator] .amplify-label': {
            display: 'none !important',
          },

          /* ── Field spacing ── */
          '[data-amplify-authenticator] .amplify-field': {
            marginBottom: '18px !important',
            width: '100% !important',
            display: 'block !important',
          },

          /* ── Input fields — light lavender bg ── */
          '[data-amplify-authenticator] .amplify-input': {
            backgroundColor: '#FFFFFF !important',
            border: '1.5px solid #E5E7EB !important',
            borderRadius: '12px !important',
            fontFamily: 'Poppins, sans-serif !important',
            fontSize: '15px !important',
            fontWeight: '500 !important',
            color: '#0B1B2E !important',
            paddingTop: '14px !important',
            paddingBottom: '14px !important',
            paddingLeft: '18px !important',
            paddingRight: '18px !important',
            width: '100% !important',
            boxSizing: 'border-box !important',
            transition: 'border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.25s ease !important',
            outline: 'none !important',
          },
          '[data-amplify-authenticator] .amplify-input::placeholder': {
            color: '#B0BBCC !important',
            fontWeight: '400 !important',
          },
          '[data-amplify-authenticator] .amplify-input:hover': {
            borderColor: 'rgba(67,97,238,0.35) !important',
            backgroundColor: '#FFFFFF !important',
          },
          '[data-amplify-authenticator] .amplify-input:focus': {
            borderColor: '#4361EE !important',
            backgroundColor: '#FFFFFF !important',
            boxShadow: '0 0 0 4px rgba(67,97,238,0.1) !important',
            outline: 'none !important',
          },

          /* ── Password field group ── */
          '[data-amplify-authenticator] .amplify-field-group': {
            borderRadius: '12px !important',
            border: '1.5px solid #E5E7EB !important',
            backgroundColor: '#FFFFFF !important',
            overflow: 'hidden !important',
            width: '100% !important',
            display: 'flex !important',
            alignItems: 'stretch !important',
            boxSizing: 'border-box !important',
            transition: 'border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.25s ease !important',
          },
          '[data-amplify-authenticator] .amplify-field-group:hover': {
            borderColor: 'rgba(67,97,238,0.35) !important',
            backgroundColor: '#FFFFFF !important',
          },
          '[data-amplify-authenticator] .amplify-field-group:focus-within': {
            borderColor: '#4361EE !important',
            backgroundColor: '#FFFFFF !important',
            boxShadow: '0 0 0 4px rgba(67,97,238,0.1) !important',
          },
          '[data-amplify-authenticator] .amplify-field-group .amplify-input': {
            borderRadius: '0 !important',
            border: 'none !important',
            backgroundColor: 'transparent !important',
            boxShadow: 'none !important',
            padding: '13px 16px !important',
          },
          '[data-amplify-authenticator] .amplify-field-group .amplify-input:hover': {
            borderColor: 'transparent !important',
            backgroundColor: 'transparent !important',
          },
          '[data-amplify-authenticator] .amplify-field-group .amplify-input:focus': {
            borderColor: 'transparent !important',
            backgroundColor: 'transparent !important',
            boxShadow: 'none !important',
          },
          '[data-amplify-authenticator] .amplify-field-group__outer-end .amplify-button': {
            backgroundColor: 'transparent !important',
            border: 'none !important',
            borderLeft: '1.5px solid rgba(0,0,0,0.07) !important',
            color: '#8896A5 !important',
            padding: '0 14px !important',
            transition: 'color 0.2s, background-color 0.2s !important',
          },
          '[data-amplify-authenticator] .amplify-field-group__outer-end .amplify-button:hover': {
            color: '#0B1B2E !important',
            backgroundColor: 'rgba(11,27,46,0.04) !important',
          },

          /* ── Primary button ── */
          '[data-amplify-authenticator] .amplify-button--primary': {
            background: '#000000 !important',
            borderColor: 'transparent !important',
            borderRadius: '12px !important',
            fontFamily: 'Poppins, sans-serif !important',
            fontSize: '14px !important',
            fontWeight: '600 !important',
            textTransform: 'none !important',
            letterSpacing: '0.2px !important',
            paddingTop: '11px !important',
            paddingBottom: '11px !important',
            paddingLeft: '24px !important',
            paddingRight: '24px !important',
            height: 'auto !important',
            color: '#FFFFFF !important',
            transition: 'background 0.2s ease !important',
            boxShadow: 'none !important',
            width: '100% !important',
            display: 'flex !important',
            alignItems: 'center !important',
            justifyContent: 'center !important',
            textAlign: 'center !important',
            marginTop: '8px !important',
            boxSizing: 'border-box !important',
          },
          '[data-amplify-authenticator] .amplify-button--primary:hover': {
            background: '#222222 !important',
            transform: 'none !important',
            boxShadow: 'none !important',
          },
          '[data-amplify-authenticator] .amplify-button--primary:active': {
            transform: 'none !important',
            boxShadow: 'none !important',
          },
          '[data-amplify-authenticator] .amplify-button--primary:disabled': {
            opacity: '0.6 !important',
            cursor: 'not-allowed !important',
            transform: 'none !important',
          },

          /* ── Amplify default link buttons (hidden by custom footer on sign-in, kept for other steps) ── */
          '[data-amplify-authenticator] .amplify-button--link': {
            color: '#4361EE !important',
            fontFamily: 'Poppins, sans-serif !important',
            fontWeight: '500 !important',
            fontSize: '13px !important',
            textDecoration: 'none !important',
            borderRadius: '8px !important',
            paddingTop: '5px !important',
            paddingBottom: '5px !important',
            paddingLeft: '8px !important',
            paddingRight: '8px !important',
            transition: 'background-color 0.2s, color 0.2s !important',
          },
          '[data-amplify-authenticator] .amplify-button--link:hover': {
            backgroundColor: 'rgba(67,97,238,0.07) !important',
            color: '#3A52D4 !important',
          },

          /* ── Tab navigation ── */
          '[data-amplify-authenticator] .amplify-tabs-item': {
            fontFamily: 'Poppins, sans-serif !important',
            fontWeight: '600 !important',
            fontSize: '14px !important',
            color: '#8896A5 !important',
            borderBottom: '2px solid transparent !important',
            transition: 'color 0.2s, border-color 0.2s !important',
            padding: '10px 16px !important',
          },
          '[data-amplify-authenticator] [data-amplify-tab][aria-selected="true"]': {
            color: '#0B1B2E !important',
            borderBottomColor: '#0B1B2E !important',
            backgroundColor: 'transparent !important',
          },
          '[data-amplify-authenticator] .amplify-tabs-item:hover': {
            color: '#0B1B2E !important',
            backgroundColor: 'rgba(11,27,46,0.03) !important',
          },

          /* ── Alert / error banner ── */
          '[data-amplify-authenticator] .amplify-alert--error': {
            background: 'rgba(238,45,65,0.06) !important',
            border: '1px solid rgba(238,45,65,0.22) !important',
            borderRadius: '12px !important',
            fontFamily: 'Poppins, sans-serif !important',
            fontSize: '13px !important',
            color: '#C41E30 !important',
            padding: '12px 16px !important',
            marginBottom: '20px !important',
          },

          /* ── Inline validation errors ── */
          '[data-amplify-authenticator] .amplify-text--error': {
            fontFamily: 'Poppins, sans-serif !important',
            fontSize: '12px !important',
            color: '#EE2D41 !important',
            marginTop: '5px !important',
            display: 'flex !important',
            alignItems: 'center !important',
            gap: '4px !important',
          },

          /* ── Generic text ── */
          '[data-amplify-authenticator] .amplify-text': {
            fontFamily: 'Poppins, sans-serif !important',
            color: '#6B7A8D !important',
          },

          /* ── Checkbox (MFA / remember device) ── */
          '[data-amplify-authenticator] .amplify-checkbox__button': {
            borderRadius: '6px !important',
            borderColor: '#CBD3DE !important',
            transition: 'border-color 0.2s, box-shadow 0.2s !important',
          },
          '[data-amplify-authenticator] .amplify-checkbox__button:focus-within': {
            boxShadow: '0 0 0 3px rgba(11,27,46,0.1) !important',
            borderColor: '#0B1B2E !important',
          },
          '[data-amplify-authenticator] .amplify-checkbox__icon': {
            backgroundColor: '#0B1B2E !important',
          },
        }} />
        <Toaster position="top-right" />
        <ThemeProviderAmplify theme={customTheme}>
          <BrowserRouter>
            <Provider store={store}>
              <PersistGate persistor={persistor} loading={<CircularProgress />}>
                <Authenticator.Provider>
                  <AlertProvider>
                    <AppRoutes />
                  </AlertProvider>
                </Authenticator.Provider>
              </PersistGate>
            </Provider>
          </BrowserRouter>
        </ThemeProviderAmplify>
      </ThemeProvider>
    </React.Fragment>
  );
}

export default App;
